// «Mi día» — pantalla de la camarera en el celular. Hotel → Operación → Mi día.
// 948: el dispositivo tiene la sesión ERP del hotel; la camarera se identifica
// con su PIN (hotel.hk_entrar → token de 12 h, guardado en sessionStorage).
// Ve SOLO sus habitaciones de hoy, ya ordenadas por prioridad, y marca
// Empezar → Pausa/Seguir → Terminar con botones grandes. Cada evento lleva un
// client_uuid y la hora del dispositivo: si no hay señal queda en una cola
// local y se reenvía solo (el servidor ignora los repetidos).
// La primera vez muestra el aviso de privacidad (LOPDP) y guarda el acuse.
// 949: al terminar, checklist del tipo de limpieza (se desmarca lo que faltó);
// «Algo roto» con foto abre la orden de mantenimiento; la camarera ve el
// resultado de la inspección y deja su observación. Cola y última copia de
// «Mi día» en IndexedDB (hkOffline.ts): sin señal se sigue trabajando.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  Bath, BedDouble, BellOff, Brush, Camera, Check, CloudOff, Delete, Droplets, Layers, Lightbulb, Lock, LogIn, LogOut, Package, Pause, Play,
  QrCode, ShieldCheck, Sparkles, Star, Trash2, Tv, Wind, Wine, Wrench, X, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { QK, hotelRpc, MOTIVO_PAUSA, etiquetaPiso, type HkHabitacion } from './shared';
import { ModalMinibar, ModalObjeto } from './Housekeeping';
import { comprimirFoto, hkGet, hkSet } from './hkOffline';
import { EscanerQR } from '@/components/ui/EscanerQR';
import { haceCuanto, type AreaEstado } from './HousekeepingInsumos';
import { uuid } from '@/lib/uuid';

type EstadoTarea = 'pendiente' | 'en_proceso' | 'pausa' | 'terminada';
interface TareaMiDia {
  tarea_id: string; habitacion_id: string; numero: string; piso: string | null; tipo: string; tarea_tipo: 'salida' | 'ocupada' | 'libre' | 'profunda';
  creditos: number; estado: EstadoTarea; estado_limpieza: string; ocupada: boolean; huesped: string | null; sale_hoy: boolean;
  llega_hoy: boolean; llegada_hora: string | null; vip: boolean; no_molestar: boolean; inicio_at: string | null; fin_at: string | null;
  minutos: number | null; estandar: number; pausa_motivo: string | null; prioridad: number; prioridad_motivo: string | null;
  relimpieza: number; inspeccion_estado: string | null; inspeccion: InspeccionMiDia | null; qr?: string;
}
interface InspeccionMiDia { id: string; resultado: 'aprobada' | 'rechazada'; puntaje: number; defectos: string[] | null; nota: string | null; observacion: string | null }
type PasoChecklist = { id: string; texto: string; icono: string };
interface MiDiaResp {
  camarera: string; fecha: string; aviso_pendiente: boolean; tope: number; tareas: TareaMiDia[];
  checklist?: Partial<Record<'salida' | 'ocupada' | 'libre' | 'profunda', PasoChecklist[]>>;
  areas?: AreaEstado[];
}
type Evento = 'empezar' | 'pausa' | 'seguir' | 'terminar';
interface ColaEvento { kind: 'evento'; tarea: string; evento: Evento; motivo: string | null; datos?: Record<string, unknown>; client_uuid: string; at: string }
interface ColaAveria { kind: 'averia'; hab: string; categoria: string; descripcion: string; foto: string | null; client_uuid: string }
interface ColaRonda { kind: 'ronda'; area: string; faltantes: string[]; observacion: string; client_uuid: string; at: string }
type EnCola = ColaEvento | ColaAveria | ColaRonda;

const TIPO_LBL: Record<string, string> = { salida: 'Salida', ocupada: 'Ocupada (sigue)', libre: 'Libre sucia', profunda: 'Limpieza profunda' };
const tras: Record<Evento, EstadoTarea> = { empezar: 'en_proceso', pausa: 'pausa', seguir: 'en_proceso', terminar: 'terminada' };
const leer = <T,>(k: string, def: T, store: Storage): T => { try { const v = store.getItem(k); return v ? JSON.parse(v) as T : def; } catch { return def; } };
const guardar = (k: string, v: unknown, store: Storage) => { try { if (v == null) store.removeItem(k); else store.setItem(k, JSON.stringify(v)); } catch { /* sin almacenamiento */ } };
// Sin respuesta del servidor = sin señal (se encola); con respuesta = error de negocio.
const sinSenal = (e: unknown) => axios.isAxiosError(e) && !e.response;

export function MiDiaHotel() {
  const { user } = useAuth();
  const emp = user?.empresa?.id ?? 'na';
  const kTok = `hk_token_${emp}`; const kCola = `hk_cola_${emp}`;
  const [token, setTokenSt] = useState<string | null>(() => leer<string | null>(kTok, null, sessionStorage));
  const setToken = (t: string | null) => { guardar(kTok, t, sessionStorage); setTokenSt(t); };
  useEffect(() => { setTokenSt(leer<string | null>(kTok, null, sessionStorage)); }, [kTok]);

  if (!token) return <Entrada onEntrar={setToken} />;
  return <Dia token={token} kCola={kCola} onSalir={() => setToken(null)} />;
}

// ── Entrada: elegir nombre y PIN ───────────────────────────────────────────
function Entrada({ onEntrar }: { onEntrar: (t: string) => void }) {
  const { data: lista = [], isLoading } = useQuery<{ id: string; nombre: string }[]>({
    queryKey: [QK, 'hk-entrada'], queryFn: () => hotelRpc('hk_camareras_entrada', {}),
  });
  const [quien, setQuien] = useState<{ id: string; nombre: string } | null>(null);
  const [pin, setPin] = useState('');
  const entrar = useMutation({
    mutationFn: () => hotelRpc<{ ok: boolean; error?: string; token?: string }>('hk_entrar', { _camarera: quien?.id, _pin: pin }),
    onSuccess: (r) => { if (r.ok && r.token) onEntrar(r.token); else { toast.error(r.error ?? 'No se pudo entrar'); setPin(''); } },
    onError: (e) => { toast.error(getErrorMessage(e)); setPin(''); },
  });

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <h1 className="text-center text-2xl font-bold">Mi día</h1>
      {!quien ? (
        <>
          <p className="text-center text-muted-foreground">Toque su nombre</p>
          {isLoading && <p className="text-center text-sm text-muted-foreground">Cargando…</p>}
          {!isLoading && lista.length === 0 && (
            <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              No hay camareras con PIN. La ama de llaves las crea en Housekeeping → Camareras.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {lista.map((c) => (
              <button key={c.id} type="button" onClick={() => { setQuien(c); setPin(''); }}
                className="rounded-2xl border-2 bg-card px-3 py-6 text-lg font-semibold shadow-sm active:scale-95">
                {c.nombre}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-center text-lg">Hola, <strong>{quien.nombre}</strong>. Escriba su PIN</p>
          <div className="flex justify-center gap-3" aria-label="PIN">
            {Array.from({ length: Math.max(4, pin.length) }, (_, i) => <span key={i} className={`h-4 w-4 rounded-full border-2 border-primary ${pin.length > i ? 'bg-primary' : ''}`} />)}
          </div>
          <div className="mx-auto grid max-w-xs grid-cols-3 gap-3">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button key={d} type="button" disabled={entrar.isPending} onClick={() => setPin((p) => (p.length < 6 ? p + d : p))}
                className="h-16 rounded-2xl border-2 bg-card text-2xl font-semibold active:scale-95">{d}</button>
            ))}
            <button type="button" onClick={() => { setQuien(null); setPin(''); }} className="h-16 rounded-2xl text-sm text-muted-foreground">No soy yo</button>
            <button type="button" disabled={entrar.isPending} onClick={() => setPin((p) => (p.length < 6 ? `${p}0` : p))}
              className="h-16 rounded-2xl border-2 bg-card text-2xl font-semibold active:scale-95">0</button>
            <button type="button" onClick={() => setPin((p) => p.slice(0, -1))} className="flex h-16 items-center justify-center rounded-2xl" aria-label="Borrar">
              <Delete className="h-7 w-7" />
            </button>
          </div>
          {pin.length >= 4 && <Button className="h-14 w-full text-lg" isLoading={entrar.isPending} onClick={() => entrar.mutate()}><LogIn className="mr-2 h-4 w-4" /> Entrar</Button>}
        </>
      )}
    </div>
  );
}

// ── Mi día ──────────────────────────────────────────────────────────────────
const ICONO_PASO: Record<string, typeof Check> = {
  bed: BedDouble, bath: Bath, towel: Layers, soap: Droplets, sparkles: Sparkles, broom: Brush, trash: Trash2, wine: Wine, package: Package, check: Check,
};
const CATEGORIAS: { k: string; l: string; icono: typeof Check }[] = [
  { k: 'agua', l: 'Agua / fuga', icono: Droplets }, { k: 'luz', l: 'Luz', icono: Lightbulb }, { k: 'aire', l: 'Aire acond.', icono: Wind },
  { k: 'tv', l: 'TV / control', icono: Tv }, { k: 'mueble', l: 'Mueble / cerradura', icono: Lock }, { k: 'bano', l: 'Baño', icono: Bath },
  { k: 'otro', l: 'Otro', icono: Wrench },
];

function Dia({ token, kCola, onSalir }: { token: string; kCola: string; onSalir: () => void }) {
  const qc = useQueryClient();
  const kFoto = `hk_midia_${token}`;
  const [cola, setColaSt] = useState<EnCola[]>([]);
  const [colaLista, setColaLista] = useState(false);
  const setCola = useCallback((c: EnCola[]) => { void hkSet(kCola, c.length ? c : null); setColaSt(c); }, [kCola]);
  useEffect(() => { void hkGet<EnCola[]>(kCola).then((c) => { setColaSt(c ?? []); setColaLista(true); }); }, [kCola]);
  const [sinConexion, setSinConexion] = useState(false);
  const [pausaDe, setPausaDe] = useState<string | null>(null);
  const [terminarDe, setTerminarDe] = useState<string | null>(null);
  const [faltan, setFaltan] = useState<string[]>([]);
  const [averiaDe, setAveriaDe] = useState<TareaMiDia | null>(null);
  const [escanear, setEscanear] = useState(false);
  const [rondaDe, setRondaDe] = useState<AreaEstado | null>(null);
  const [minibar, setMinibar] = useState<HkHabitacion | null>(null);
  const [objeto, setObjeto] = useState<HkHabitacion | null>(null);

  // Sin señal se muestra la última copia guardada de «Mi día».
  const { data, error } = useQuery<MiDiaResp>({
    queryKey: [QK, 'hk-mi-dia', token],
    queryFn: async () => {
      try {
        const r = await hotelRpc<MiDiaResp>('hk_mi_dia', { _token: token });
        setSinConexion(false); void hkSet(kFoto, r);
        return r;
      } catch (e) {
        if (sinSenal(e)) { const copia = await hkGet<MiDiaResp>(kFoto); if (copia) { setSinConexion(true); return copia; } }
        throw e;
      }
    },
    refetchInterval: 30000, staleTime: 0, retry: (n, e) => !getErrorMessage(e).startsWith('SESION') && n < 2,
  });
  useEffect(() => {
    if (error && getErrorMessage(error).startsWith('SESION')) { toast.info('Su sesión terminó: ingrese su PIN de nuevo'); onSalir(); }
  }, [error, onSalir]);

  const salir = async () => { try { await hotelRpc('hk_salir', { _token: token }); } catch { /* sin señal: igual se sale */ } onSalir(); };

  // Envía un cambio; sin señal queda en la cola.
  const enviar = async (e: EnCola): Promise<'ok' | 'cola' | 'error'> => {
    try {
      if (e.kind === 'ronda') {
        await hotelRpc('hk_ronda_registrar', { _token: token, _area: e.area, _faltantes: e.faltantes, _observacion: e.observacion, _client_uuid: e.client_uuid, _at_dispositivo: e.at });
      } else if (e.kind === 'averia') {
        await hotelRpc('hk_averia', { _token: token, _hab: e.hab, _categoria: e.categoria, _descripcion: e.descripcion, _foto: e.foto, _mime: e.foto ? 'image/jpeg' : null, _client_uuid: e.client_uuid });
      } else {
        await hotelRpc('hk_evento', { _token: token, _tarea: e.tarea, _evento: e.evento, _motivo: e.motivo, _client_uuid: e.client_uuid, _at_dispositivo: e.at, _datos: e.datos ?? null });
      }
      return 'ok';
    } catch (err) {
      if (sinSenal(err)) return 'cola';
      toast.error(getErrorMessage(err));
      return 'error';
    }
  };
  const encolarOEnviar = async (e: EnCola, okMsg?: string) => {
    if (cola.length) { setCola([...cola, e]); return; } // conserva el orden: detrás de lo pendiente
    const r = await enviar(e);
    if (r === 'cola') { setCola([...cola, e]); toast.info('Sin señal: se enviará solo cuando vuelva'); }
    if (r === 'ok') { if (okMsg) toast.success(okMsg); void qc.invalidateQueries({ queryKey: [QK] }); }
  };
  const evento = (tarea: string, ev: Evento, motivo: string | null = null, datos?: Record<string, unknown>) =>
    encolarOEnviar({ kind: 'evento', tarea, evento: ev, motivo, datos, client_uuid: uuid(), at: new Date().toISOString() },
      ev === 'terminar' ? '¡Habitación lista! Recepción ya fue avisada' : undefined);

  // Reenvío de la cola: al volver la señal y cada 15 s.
  useEffect(() => {
    if (!colaLista || !cola.length) return;
    let vivo = true;
    const vaciar = async () => {
      let pendiente = [...cola];
      while (pendiente.length && vivo) {
        const r = await enviar(pendiente[0]);
        if (r === 'cola') break;
        pendiente = pendiente.slice(1); // ok o error de negocio: sale de la cola
      }
      if (vivo && pendiente.length !== cola.length) { setCola(pendiente); void qc.invalidateQueries({ queryKey: [QK] }); }
    };
    const t = setInterval(() => void vaciar(), 15000);
    window.addEventListener('online', vaciar);
    return () => { vivo = false; clearInterval(t); window.removeEventListener('online', vaciar); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cola, colaLista]);

  // Estado con lo que aún está en la cola aplicado encima (para no confundir a la camarera).
  const tareas = useMemo(() => (data?.tareas ?? []).map((t) => {
    const ult = [...cola].reverse().find((c): c is ColaEvento => c.kind === 'evento' && c.tarea === t.tarea_id);
    return ult ? { ...t, estado: tras[ult.evento], pausa_motivo: ult.motivo ?? t.pausa_motivo } : t;
  }), [data, cola]);

  if (!data) return <div className="p-6 text-center text-muted-foreground">Cargando…</div>;
  if (data.aviso_pendiente) return <Aviso token={token} onListo={() => void qc.invalidateQueries({ queryKey: [QK, 'hk-mi-dia'] })} />;
  if (averiaDe) {
    return <AlgoRoto t={averiaDe} onCancelar={() => setAveriaDe(null)}
      onEnviar={(categoria, descripcion, foto) => {
        setAveriaDe(null);
        void encolarOEnviar({ kind: 'averia', hab: averiaDe.habitacion_id, categoria, descripcion, foto, client_uuid: uuid() }, 'Avisado a mantenimiento');
      }} />;
  }

  // 950: QR de la puerta → empieza la limpieza de esa habitación (también sin señal).
  const leerQr = (texto: string) => {
    const qr = texto.trim().replace(/^K2E-HK:/i, '');
    const t = tareas.find((x) => x.qr === qr);
    setEscanear(false);
    if (!t) { toast.error('Esa habitación no está en su lista de hoy'); return; }
    if (t.estado === 'pendiente') { void evento(t.tarea_id, 'empezar', null, { qr }); toast.success(`Empezó la ${t.numero}`); }
    else if (t.estado === 'pausa') void evento(t.tarea_id, 'seguir');
    else toast.info(`La ${t.numero} ya está ${t.estado === 'terminada' ? 'lista' : 'en limpieza'}`);
  };
  if (rondaDe) {
    return <Ronda area={rondaDe} onCancelar={() => setRondaDe(null)}
      onRegistrar={(faltantes, observacion) => {
        setRondaDe(null);
        void encolarOEnviar({ kind: 'ronda', area: rondaDe.id, faltantes, observacion, client_uuid: uuid(), at: new Date().toISOString() }, `Ronda de ${rondaDe.nombre} registrada`);
      }} />;
  }
  // Minibar y objeto olvidado: pantalla completa del celular (sin ventanas encima).
  if (minibar) return <div className="mx-auto max-w-lg p-3"><ModalMinibar hab={minibar} onClose={() => setMinibar(null)} onDone={() => setMinibar(null)} /></div>;
  if (objeto) return <div className="mx-auto max-w-lg p-3"><ModalObjeto hab={objeto} onClose={() => setObjeto(null)} onDone={() => setObjeto(null)} /></div>;
  if (escanear) return <div className="p-3"><EscanerQR integrado titulo="Escanee el QR de la puerta" onLeer={leerQr} onCerrar={() => setEscanear(false)} /></div>;

  const total = tareas.length;
  const hechas = tareas.filter((t) => t.estado === 'terminada').length;
  const crTot = tareas.reduce((a, t) => a + Number(t.creditos), 0);
  const crHechos = tareas.filter((t) => t.estado === 'terminada').reduce((a, t) => a + Number(t.creditos), 0);
  const aHab = (t: TareaMiDia): HkHabitacion => ({
    id: t.habitacion_id, numero: t.numero, piso: t.piso, tipo: t.tipo, estado_limpieza: t.estado_limpieza, estado_mant: 'ok',
    ocupada: t.ocupada, huesped: t.huesped, sale_hoy: t.sale_hoy, llega_hoy: t.llega_hoy, llegada_codigo: null,
  });

  return (
    <div className="mx-auto max-w-lg space-y-3 p-3">
      <div className="sticky top-0 z-10 -mx-3 space-y-2 border-b bg-background/95 px-3 py-2 backdrop-blur">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">{data.camarera}</div>
            <div className="text-xs text-muted-foreground">Hoy · {hechas} de {total} habitaciones · {crHechos} de {crTot} créditos</div>
          </div>
          <div className="flex gap-1">
            {tareas.some((t) => t.qr) && <Button variant="outline" size="sm" onClick={() => setEscanear(true)}><QrCode className="mr-1 h-4 w-4" /> Escanear puerta</Button>}
            <Button variant="outline" size="sm" onClick={() => void salir()}><LogOut className="mr-1 h-4 w-4" /> Salir</Button>
          </div>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${total ? (hechas / total) * 100 : 0}%` }} />
        </div>
        {(cola.length > 0 || sinConexion) && (
          <div className="flex items-center gap-2 rounded-md bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
            <CloudOff className="h-3.5 w-3.5" /> Sin señal{cola.length ? `: ${cola.length} cambio(s) por enviar, se envían solos` : ': mostrando lo último guardado'}.
          </div>
        )}
      </div>

      {total === 0 && (
        <div className="rounded-2xl border-2 border-dashed p-8 text-center text-muted-foreground">
          Todavía no tiene habitaciones asignadas hoy. Avise a la ama de llaves.
        </div>
      )}

      {tareas.map((t) => {
        const urgente = !!t.prioridad_motivo && t.estado !== 'terminada';
        const insp = t.inspeccion;
        const pasos = data.checklist?.[t.tarea_tipo] ?? [];
        return (
          <div key={t.tarea_id} className={`rounded-2xl border-2 p-4 shadow-sm ${
            t.estado === 'terminada' ? 'border-emerald-300 bg-emerald-50/50 opacity-80 dark:bg-emerald-950/20'
              : t.estado === 'en_proceso' ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900'
              : t.estado === 'pausa' ? 'border-amber-400' : urgente ? 'border-red-400' : 'border-border'}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-4xl font-bold leading-none">{t.numero}</div>
                <div className="mt-1 text-sm text-muted-foreground">{TIPO_LBL[t.tarea_tipo]} · {t.creditos} cr · {etiquetaPiso(t.piso)}</div>
              </div>
              <div className="flex flex-col items-end gap-1 text-xs font-semibold">
                {urgente && <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-white"><Zap className="h-3.5 w-3.5" /> ¡Urgente!</span>}
                {t.vip && <span className="inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2.5 py-1 text-yellow-950"><Star className="h-3.5 w-3.5" /> VIP</span>}
                {t.no_molestar && <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-2.5 py-1 text-white"><BellOff className="h-3.5 w-3.5" /> No molestar</span>}
              </div>
            </div>
            {urgente && <div className="mt-1 text-sm font-medium text-red-700 dark:text-red-400">{t.prioridad_motivo}</div>}
            {!urgente && t.llega_hoy && <div className="mt-1 text-sm text-sky-700 dark:text-sky-400">Llega huésped hoy{t.llegada_hora ? ` a las ${t.llegada_hora.slice(0, 5)}` : ''}</div>}
            {t.huesped && t.ocupada && <div className="mt-1 truncate text-sm text-muted-foreground">👤 {t.huesped}{t.sale_hoy ? ' · sale hoy' : ''}</div>}

            {insp && <ResultadoInspeccion token={token} insp={insp} />}

            <div className="mt-3">
              {t.estado === 'pendiente' && (
                <Button className="h-16 w-full bg-blue-600 text-xl text-white hover:bg-blue-700" onClick={() => void evento(t.tarea_id, 'empezar')}>
                  <Play className="mr-2 h-6 w-6" /> {t.relimpieza > 0 ? 'Empezar re-limpieza' : 'Empezar'}
                </Button>
              )}
              {(t.estado === 'en_proceso' || t.estado === 'pausa') && pausaDe !== t.tarea_id && terminarDe !== t.tarea_id && (
                <div className="grid grid-cols-2 gap-2">
                  {t.estado === 'en_proceso'
                    ? <Button className="h-16 bg-amber-500 text-lg text-amber-950 hover:bg-amber-600" onClick={() => setPausaDe(t.tarea_id)}><Pause className="mr-2 h-5 w-5" /> Pausa</Button>
                    : <Button className="h-16 bg-blue-600 text-lg text-white hover:bg-blue-700" onClick={() => void evento(t.tarea_id, 'seguir')}><Play className="mr-2 h-5 w-5" /> Seguir</Button>}
                  <Button className="h-16 bg-emerald-600 text-lg text-white hover:bg-emerald-700" onClick={() => { setTerminarDe(t.tarea_id); setFaltan([]); }}><Check className="mr-2 h-6 w-6" /> Terminar</Button>
                </div>
              )}
              {t.estado === 'pausa' && pausaDe !== t.tarea_id && t.pausa_motivo && (
                <div className="mt-1 text-center text-sm text-amber-700 dark:text-amber-400">En pausa: {MOTIVO_PAUSA[t.pausa_motivo] ?? t.pausa_motivo}</div>
              )}
              {pausaDe === t.tarea_id && (
                <div className="space-y-2">
                  <div className="text-center text-sm font-medium">¿Por qué pausa?</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(MOTIVO_PAUSA).map(([k, l]) => (
                      <Button key={k} variant="outline" className="h-14 text-base" onClick={() => { setPausaDe(null); void evento(t.tarea_id, 'pausa', k); }}>{l}</Button>
                    ))}
                  </div>
                  <Button variant="ghost" className="w-full" onClick={() => setPausaDe(null)}>Volver</Button>
                </div>
              )}
              {terminarDe === t.tarea_id && (
                <div className="space-y-2">
                  <div className="text-center text-lg font-medium">¿Terminó la {t.numero}?</div>
                  {pasos.length > 0 && <div className="text-center text-xs text-muted-foreground">Toque lo que NO pudo hacer</div>}
                  <div className="grid grid-cols-1 gap-1.5">
                    {pasos.map((p) => {
                      const I = ICONO_PASO[p.icono] ?? Check;
                      const falta = faltan.includes(p.id);
                      return (
                        <button key={p.id} type="button" onClick={() => setFaltan(falta ? faltan.filter((x) => x !== p.id) : [...faltan, p.id])}
                          className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 text-left text-base ${falta ? 'border-red-400 bg-red-50 text-red-800 line-through dark:bg-red-950/30 dark:text-red-300' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20'}`}>
                          <I className="h-6 w-6 shrink-0" />
                          <span className="flex-1">{p.texto}</span>
                          {falta ? <X className="h-6 w-6 text-red-600" /> : <Check className="h-6 w-6 text-emerald-600" />}
                        </button>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" className="h-14 text-lg" onClick={() => setTerminarDe(null)}>Volver</Button>
                    <Button className="h-14 bg-emerald-600 text-lg text-white hover:bg-emerald-700"
                      onClick={() => { setTerminarDe(null); void evento(t.tarea_id, 'terminar', null, { faltantes: faltan }); }}>Sí, lista</Button>
                  </div>
                </div>
              )}
              {t.estado === 'terminada' && (
                <div className="flex items-center justify-center gap-2 py-2 text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                  <Check className="h-6 w-6" /> Lista{t.minutos != null ? ` · ${t.minutos} min` : ''}
                </div>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <button type="button" className="rounded-lg border border-red-300 px-3 py-2 font-medium text-red-700 dark:text-red-400" onClick={() => setAveriaDe(t)}>
                <Wrench className="mr-1 inline h-4 w-4" />Algo roto
              </button>
              {t.estado !== 'terminada' && <>
                {t.ocupada && <button type="button" className="rounded-lg border px-3 py-2" onClick={() => setMinibar(aHab(t))}><Wine className="mr-1 inline h-4 w-4" />Minibar</button>}
                <button type="button" className="rounded-lg border px-3 py-2" onClick={() => setObjeto(aHab(t))}><Package className="mr-1 inline h-4 w-4" />Objeto olvidado</button>
                <VeoDistinto hab={t.habitacion_id} ocupada={t.ocupada} />
              </>}
            </div>
          </div>
        );
      })}

      {!!data.areas?.length && (
        <div className="space-y-2 rounded-2xl border-2 p-3">
          <div className="text-lg font-semibold">Áreas públicas</div>
          {data.areas.map((a) => {
            const hecha = cola.some((c) => c.kind === 'ronda' && c.area === a.id);
            const vencida = a.vencida && !hecha;
            return (
              <button key={a.id} type="button" onClick={() => setRondaDe(a)}
                className={`flex w-full items-center justify-between rounded-xl border-2 px-3 py-3 text-left ${vencida ? 'border-red-400 bg-red-50 dark:bg-red-950/30' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20'}`}>
                <span className="text-base font-medium">{a.nombre}</span>
                <span className={`text-sm ${vencida ? 'font-semibold text-red-700 dark:text-red-400' : 'text-muted-foreground'}`}>{hecha ? 'recién hecha' : vencida ? `¡Toca ya! (${haceCuanto(a.ultima_at)})` : haceCuanto(a.ultima_at)}</span>
              </button>
            );
          })}
        </div>
      )}

    </div>
  );
}

// Ronda de un área pública: pasos (se toca lo que no se pudo hacer) y nota.
function Ronda({ area, onCancelar, onRegistrar }: { area: AreaEstado; onCancelar: () => void; onRegistrar: (faltantes: string[], observacion: string) => void }) {
  const [faltan, setFaltan] = useState<string[]>([]);
  const [obs, setObs] = useState('');
  return (
    <div className="mx-auto max-w-lg space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Ronda · {area.nombre}</h2>
        <Button variant="ghost" size="sm" onClick={onCancelar}><X className="h-5 w-5" /></Button>
      </div>
      <div className="text-center text-xs text-muted-foreground">Toque lo que NO pudo hacer</div>
      <div className="grid gap-1.5">
        {(area.pasos ?? []).map((p) => {
          const falta = faltan.includes(p);
          return (
            <button key={p} type="button" onClick={() => setFaltan(falta ? faltan.filter((x) => x !== p) : [...faltan, p])}
              className={`flex items-center justify-between rounded-xl border-2 px-3 py-3 text-left text-base ${falta ? 'border-red-400 bg-red-50 text-red-800 line-through dark:bg-red-950/30 dark:text-red-300' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20'}`}>
              {p}{falta ? <X className="h-6 w-6 text-red-600" /> : <Check className="h-6 w-6 text-emerald-600" />}
            </button>
          );
        })}
      </div>
      <input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Novedad (opcional)" className="h-12 w-full rounded-xl border bg-background px-3 text-base" />
      <Button className="h-14 w-full text-lg" onClick={() => onRegistrar(faltan, obs.trim())}>Registrar ronda</Button>
    </div>
  );
}

// Resultado de la inspección: la camarera lo ve y puede dejar su observación.
function ResultadoInspeccion({ token, insp }: { token: string; insp: InspeccionMiDia }) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');
  const [abierta, setAbierta] = useState(false);
  const observar = useMutation({
    mutationFn: () => hotelRpc('hk_inspeccion_observar', { _token: token, _inspeccion: insp.id, _texto: texto }),
    onSuccess: () => { toast.success('Observación guardada'); setAbierta(false); void qc.invalidateQueries({ queryKey: [QK, 'hk-mi-dia'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rechazada = insp.resultado === 'rechazada';
  return (
    <div className={`mt-2 rounded-xl px-3 py-2 text-sm ${rechazada ? 'bg-red-50 text-red-900 dark:bg-red-950/30 dark:text-red-200' : 'bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'}`}>
      <div className="font-semibold">{rechazada ? `Inspección: hay que repasar (${insp.puntaje} puntos)` : `✓ Inspeccionada · ${insp.puntaje} puntos`}</div>
      {insp.defectos?.length ? <div>{insp.defectos.join(' · ')}</div> : null}
      {insp.nota && <div className="text-xs opacity-80">Nota: {insp.nota}</div>}
      {insp.observacion
        ? <div className="mt-1 text-xs">Su observación: {insp.observacion}</div>
        : rechazada && (abierta ? (
          <div className="mt-2 space-y-1.5">
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} placeholder="Si no está de acuerdo, explique aquí"
              className="w-full rounded-md border bg-background px-2 py-1 text-sm text-foreground" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAbierta(false)}>Cancelar</Button>
              <Button size="sm" disabled={texto.trim().length < 3} isLoading={observar.isPending} onClick={() => observar.mutate()}>Guardar</Button>
            </div>
          </div>
        ) : <button type="button" className="mt-1 text-xs underline" onClick={() => setAbierta(true)}>Dejar mi observación</button>)}
    </div>
  );
}

// «Algo roto»: categoría con iconos grandes, detalle opcional y foto de la habitación.
function AlgoRoto({ t, onCancelar, onEnviar }: { t: TareaMiDia; onCancelar: () => void; onEnviar: (categoria: string, descripcion: string, foto: string | null) => void }) {
  const [cat, setCat] = useState<string | null>(null);
  const [desc, setDesc] = useState('');
  const [foto, setFoto] = useState<string | null>(null);
  const [procesando, setProcesando] = useState(false);
  return (
    <div className="mx-auto max-w-lg space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Algo roto · {t.numero}</h2>
        <Button variant="ghost" size="sm" onClick={onCancelar}><X className="h-5 w-5" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {CATEGORIAS.map((c) => (
          <button key={c.k} type="button" onClick={() => setCat(c.k)}
            className={`flex flex-col items-center gap-1 rounded-2xl border-2 py-4 text-base font-medium ${cat === c.k ? 'border-primary bg-primary/10' : ''}`}>
            <c.icono className="h-8 w-8" /> {c.l}
          </button>
        ))}
      </div>
      <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Detalle (opcional)" className="h-12 w-full rounded-xl border bg-background px-3 text-base" />
      <label className="flex h-14 cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed text-base">
        <Camera className="h-6 w-6" /> {procesando ? 'Procesando…' : foto ? 'Cambiar foto' : 'Tomar foto (opcional)'}
        <input type="file" accept="image/*" capture="environment" className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            setProcesando(true);
            try { setFoto(await comprimirFoto(f)); } catch (err) { toast.error(getErrorMessage(err)); } finally { setProcesando(false); }
          }} />
      </label>
      {foto && <img src={foto} alt="Foto de la avería" className="max-h-56 w-full rounded-xl object-cover" />}
      <p className="text-xs text-muted-foreground">Fotografíe solo la avería, sin personas. Se abre una orden para mantenimiento.</p>
      <Button className="h-14 w-full text-lg" disabled={!cat || procesando} onClick={() => cat && onEnviar(cat, desc.trim(), foto)}>Avisar a mantenimiento</Button>
    </div>
  );
}

function VeoDistinto({ hab, ocupada }: { hab: string; ocupada: boolean }) {
  const reportar = useMutation({
    mutationFn: () => hotelRpc<{ discrepancia: boolean }>('hk_reportar', { _hab: hab, _observado: ocupada ? 'vacia' : 'ocupada' }),
    onSuccess: (r) => toast[r.discrepancia ? 'warning' : 'success'](r.discrepancia ? 'Gracias: se avisó a recepción' : 'Coincide con el sistema'),
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <button type="button" className="rounded-lg border px-3 py-2" disabled={reportar.isPending} onClick={() => reportar.mutate()}>
      La veo {ocupada ? 'vacía' : 'ocupada'}
    </button>
  );
}

// Aviso de privacidad (LOPDP): transparencia, no consentimiento. Se guarda el acuse.
function Aviso({ token, onListo }: { token: string; onListo: () => void }) {
  const aceptar = useMutation({
    mutationFn: () => hotelRpc('hk_aviso_aceptar', { _token: token }),
    onSuccess: onListo, onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-md space-y-4 p-5">
      <div className="flex items-center gap-2 text-xl font-bold"><ShieldCheck className="h-6 w-6 text-primary" /> Antes de empezar</div>
      <div className="space-y-3 text-base leading-relaxed">
        <p>El hotel registra la <strong>hora de inicio y fin de cada habitación</strong> que usted limpia, para organizar el trabajo, repartir la carga de forma justa y avisar a recepción cuando una habitación queda lista.</p>
        <p><strong>No se registra su ubicación</strong> fuera de las habitaciones ni fuera de su turno, y estos tiempos <strong>no se usan para descontar su sueldo</strong>.</p>
        <p>Puede pedir ver sus datos a la administración del hotel y pedir que se corrijan. El detalle se guarda por 12 meses.</p>
      </div>
      <Button className="h-14 w-full text-lg" isLoading={aceptar.isPending} onClick={() => aceptar.mutate()}>Entendido</Button>
    </div>
  );
}
